import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { BallMark } from '@/components/landing/ball-mark'
import { ButtonLink } from '@/components/landing/button-link'
import { CourtFloor } from '@/components/landing/court-floor'
import { setBallDirector } from '@/lib/ball-director'
import { clamp, easeOutCubic, lerp } from '@/lib/motion'
import { EVENT, NAV_LINKS } from '@/constants/event'

/** The page ends at sunset: the 3D ball sinks behind the horizon like the sun as you reach the bottom. */
export const SiteFooter: FC = (): ReactNode => {
    const stageRef = useRef<HTMLDivElement>(null)
    const sunRef = useRef<HTMLDivElement>(null)
    const horizonRef = useRef<HTMLDivElement>(null)
    const glowRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        return setBallDirector('footer', 3, ({ height }) => {
            const stage = stageRef.current
            const sun = sunRef.current
            const horizon = horizonRef.current
            if (!stage || !sun || !horizon) return null

            const stageRect = stage.getBoundingClientRect()
            if (stageRect.top > height || stageRect.bottom < 0) return null

            const sunRect = sun.getBoundingClientRect()
            const horizonY = horizon.getBoundingClientRect().top
            const radius = sunRect.width / 2
            const sunset = easeOutCubic(clamp((height - stageRect.top) / Math.max(stageRect.height, 1)))

            if (glowRef.current) glowRef.current.style.opacity = String(0.35 + sunset * 0.65)

            return {
                x: sunRect.left + radius,
                y: horizonY + lerp(-radius * 2.1, radius * 0.35, sunset),
                radius,
                clipBottom: horizonY,
                spin: 0.35
            }
        })
    }, [])

    return (
        <footer className='relative overflow-hidden bg-ink text-cream'>
            <div ref={stageRef} className='relative h-[92svh] min-h-[600px]'>
                <div aria-hidden='true' className='absolute inset-0 bg-linear-to-b from-ink via-court-deep to-court' />
                <div aria-hidden='true' className='grain-overlay absolute inset-0' />

                <div
                    ref={glowRef}
                    aria-hidden='true'
                    className='absolute left-1/2 top-[72%] aspect-square w-[150vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(214,240,74,0.4)_0%,rgba(214,240,74,0.12)_30%,transparent_62%)] opacity-40 md:left-[70%] md:w-[80vw]'
                />

                <div
                    ref={sunRef}
                    aria-hidden='true'
                    className='absolute left-1/2 top-[50%] aspect-square w-[min(56vw,300px)] -translate-x-1/2 md:left-[70%] md:w-[min(26vw,380px)]'
                />

                <div ref={horizonRef} aria-hidden='true' className='absolute inset-x-0 top-[72%] h-px bg-cream/25' />

                <div aria-hidden='true' className='absolute inset-x-0 bottom-0 top-[72%] bg-ink'>
                    <div className='absolute inset-0 text-cream/15'>
                        <CourtFloor />
                    </div>
                </div>

                <div className='relative mx-auto flex h-[72%] max-w-[1440px] flex-col justify-start px-5 pt-28 md:justify-center md:px-10 md:pt-16'>
                    <p className='font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-lime'>
                        {`${EVENT.datesCompact} · ${EVENT.city}`}
                    </p>

                    <h2 className='mt-5 max-w-4xl font-heading text-[clamp(3.4rem,8vw,7.5rem)] font-black uppercase leading-[0.84]'>
                        See you
                        <span className='serif-soft block font-serif font-normal normal-case italic text-lime'>in the kitchen.</span>
                    </h2>

                    <div className='mt-8 flex flex-wrap gap-3'>
                        <ButtonLink href='/#tickets'>
                            Join the ticket list
                        </ButtonLink>

                        <ButtonLink href='/#play' variant='outline-cream'>
                            Enter a team
                        </ButtonLink>
                    </div>
                </div>
            </div>

            <div className='relative border-t border-cream/10 bg-ink'>
                <div className='mx-auto flex max-w-[1440px] flex-col gap-8 px-5 py-10 md:flex-row md:items-center md:justify-between md:px-10'>
                    <a href='/#hero' className='flex items-center gap-2.5 self-start rounded-full focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lime'>
                        <BallMark className='size-8' />
                        <span className='font-heading text-xl font-extrabold uppercase tracking-[0.06em]'>
                            Kitchen Open
                        </span>
                    </a>

                    <nav aria-label='Footer'>
                        <ul className='flex flex-wrap gap-x-7 gap-y-3'>
                            {NAV_LINKS.map((link) => (
                                <li key={link.href}>
                                    <a
                                        href={`/${link.href}`}
                                        className='rounded-sm text-sm text-cream/70 transition-colors hover:text-lime focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lime'
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <p className='text-sm text-cream/50'>
                        {`${EVENT.venue}, ${EVENT.city}`}
                    </p>
                </div>
            </div>
        </footer>
    )
}
